import { useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Crop, ImageUp, Loader2, Maximize2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { siteImg } from "@/context/AppearanceContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_SRC = 15 * 1024 * 1024;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1048576).toFixed(2)}MB` : `${Math.round(n / 1024)}KB`);

const loadImage = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => res(img);
  img.onerror = rej;
  img.src = src;
});

const render = async (src, area, outW, outH) => {
  const img = await loadImage(src);
  const a = area || { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
  const scale = Math.min(1, outW / a.width, outH / a.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(a.width * scale);
  canvas.height = Math.round(a.height * scale);
  canvas.getContext("2d").drawImage(img, a.x, a.y, a.width, a.height, 0, 0, canvas.width, canvas.height);
  for (const q of [0.86, 0.75, 0.6]) {
    const blob = await new Promise((r) => canvas.toBlob(r, "image/webp", q));
    if (blob && blob.size <= 3 * 1024 * 1024) return { blob, w: canvas.width, h: canvas.height };
  }
  throw new Error("Gambar terlalu besar setelah dikompres");
};

export const ImageCropUpload = ({ kind, title, guide, formats, aspect, outW, outH, current, onDone, onRemove, canManage, testid }) => {
  const [src, setSrc] = useState(null);
  const [orig, setOrig] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [whole, setWhole] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) return toast.error("Format harus PNG, JPG, atau WEBP");
    if (f.size > MAX_SRC) return toast.error("File asli maksimal 15MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const img = await loadImage(reader.result);
      setOrig({ size: f.size, w: img.naturalWidth, h: img.naturalHeight });
      setSrc(reader.result);
      setZoom(1);
      setWhole(false);
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const out = await render(src, whole ? null : area, outW, outH);
      const fd = new FormData();
      fd.append("file", new File([out.blob], `${kind}.webp`, { type: "image/webp" }));
      const { data } = await api.post("/admin/appearance/upload", fd, { params: { kind }, headers: { "Content-Type": "multipart/form-data" } });
      await onDone(data);
      toast.success(`Tersimpan · ${out.w}×${out.h} WebP ${kb(out.blob.size)} (asli ${kb(orig.size)})`);
      setSrc(null);
    } catch (err) {
      toast.error(errMsg(err, err.message || "Upload gagal"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/5 bg-[#0d0d12] p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold">{title}</div>
          <div className="mt-0.5 text-[11px] text-zinc-500">Rekomendasi <span className="text-[#e5b80b]">{guide}</span> · {formats} · file asli maks 15MB, otomatis dikompres ke WebP ≤3MB</div>
        </div>
        {canManage ? (
          <div className="flex shrink-0 gap-1.5">
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" data-testid={`${testid}-file`} />
            <button type="button" onClick={() => ref.current?.click()} className="rk-btn-primary inline-flex items-center gap-1 !px-3 !py-1.5 text-xs" data-testid={`${testid}-upload`}>
              <ImageUp size={12} /> {current ? "Ganti" : "Upload"}
            </button>
            {current && onRemove ? (
              <button type="button" onClick={onRemove} className="rk-btn-ghost inline-flex items-center !px-2.5 !py-1.5 text-xs text-rose-400" data-testid={`${testid}-remove`}><Trash2 size={12} /></button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mx-auto mt-3 grid place-items-center overflow-hidden rounded-lg bg-[repeating-conic-gradient(#1a1a22_0%_25%,#121218_0%_50%)] bg-[length:16px_16px]" style={{ aspectRatio: aspect, width: `min(100%, ${Math.round(220 * aspect)}px)` }}>
        {current ? <img src={siteImg(current)} alt={title} className="h-full w-full object-contain" data-testid={`${testid}-preview`} /> : <span className="text-[11px] text-zinc-600">Belum ada gambar (memakai bawaan)</span>}
      </div>

      <Dialog open={!!src} onOpenChange={(o) => !o && !busy && setSrc(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[#101017] text-white">
          <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="text-xs text-zinc-500">Atur area gambar lalu simpan.</DialogDescription></DialogHeader>
          {orig ? <p className="text-[11px] text-zinc-400">Asli {orig.w}×{orig.h} px · {kb(orig.size)} → hasil maks {outW}×{outH} px (rasio dijaga)</p> : null}
          <div className="flex gap-2">
            {[[false, "Potong sesuai rasio", Crop], [true, "Pakai utuh", Maximize2]].map(([v, l, Icon]) => (
              <button key={l} type="button" onClick={() => setWhole(v)} data-testid={`crop-mode-${v ? "whole" : "crop"}`}
                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs ${whole === v ? "bg-[#e5b80b] font-semibold text-black" : "border border-white/10 text-zinc-400"}`}>
                <Icon size={12} /> {l}
              </button>
            ))}
          </div>
          <div className="relative h-80 overflow-hidden rounded-xl bg-black">
            {src && !whole ? (
              <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} />
            ) : src ? <img src={src} alt="Pratinjau" className="h-full w-full object-contain" data-testid="crop-whole-preview" /> : null}
          </div>
          {!whole ? (
            <label className="flex items-center gap-3 text-xs text-zinc-400">Zoom
              <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#e5b80b]" data-testid="crop-zoom" />
            </label>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setSrc(null)} disabled={busy} className="rk-btn-ghost text-sm">Batal</button>
            <button type="button" onClick={save} disabled={busy || (!whole && !area)} className="rk-btn-primary inline-flex items-center gap-2 text-sm" data-testid="crop-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ImageUp size={14} />} Simpan & Upload
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
